import { IText as OriginIText, classRegistry, Text } from "fabric"
import { EffectItem } from "@/types/common"
import type { Abortable } from 'fabric'
import { CENTER, RIGHT, LEFT, DEFAULT_SVG_FONT_SIZE } from "../constants"
import { parseAttributes } from '../parser/parseAttributes'
import {ArcTextMixin} from "@/extension/mixins/arctext.mixin";
import {ArcText} from "@/extension/object/ArcText";
import {TextMixin} from "@/extension/mixins/text.mixin";

export class IText extends OriginIText {
  public effects?: EffectItem[];

  constructor(text: string, options?: any) {
    super(text, options);
    this.effects = options.effects
    this.on('mousedblclick', this.onDblClick.bind(this))
  }

  renderEffects() {
    console.log('renderEffects:')
    this.canvas?.renderAll()
  }

  _renderChar(method: "fillText" | "strokeText", ctx: CanvasRenderingContext2D, lineIndex: number, charIndex: number, _char: string, left: number, top: number): void {
    if (this.effects) {
      // for (let i = 0; i < this.effects.length; i++) {
      for (let i = this.effects.length - 1; i >= 0; i--) {
        const item = this.effects[i]
        ctx.save();
        ctx.strokeStyle = item.stroke;
        ctx.lineJoin = item.strokeLineJoin;
        ctx.lineWidth = item.strokeWidth;
        ctx.strokeText(_char, left, top);
        ctx.restore()
      }
    }
    super._renderChar(method, ctx, lineIndex, charIndex, _char, left, top)
  }

  static async fromElement(
    element: HTMLElement,
    options?: Abortable,
    cssRules?: any
  ) {
    const parsedAttributes = parseAttributes(
      element,
      Text.ATTRIBUTE_NAMES,
      cssRules
    );
    const {
      textAnchor = LEFT as typeof LEFT | typeof CENTER | typeof RIGHT,
      textDecoration = '',
      dx = 0,
      dy = 0,
      top = 0,
      left = 0,
      fontSize = DEFAULT_SVG_FONT_SIZE,
      strokeWidth = 1,
      ...restOfOptions
    } = { ...options, ...parsedAttributes };

    const textContent = (element.textContent || '')
    // const textContent = (element.textContent || '')
    //   .replace(/^\s+|\s+$|\n+/g, '')
    //   .replace(/\s+/g, ' ');

    // this code here is probably the usual issue for SVG center find
    // this can later looked at again and probably removed.

    function isPunctuationOrSymbol(code) {
      // Sử dụng Set cho các ký tự lẻ và mảng cho các khoảng liên tiếp
      const singleCodePoints = new Set([
        0x005F, 0x00BB, 0x2019, 0x201D, 0x203A, 0x00AB, 0x2018, 0x201B, 0x201C, 0x201F, 0x2039,
        0x2022, 0x00B7, 0x2219, 0x25CF // Bullet, Middle Dot, Bullet Operator, Black Circle
        // Thêm các mã đơn lẻ khác tại đây nếu cần
      ]);

      const ranges = [
        [0x0021, 0x0023], [0x0025, 0x002A], [0x002C, 0x002F], // Basic Punctuation
        [0x003A, 0x003B], [0x003F, 0x0040], // Colon, Semicolon, Question Mark, At Sign
        [0x2010, 0x2015], // Hyphen, Non-breaking Hyphen, Figure Dash, En Dash, Em Dash, Horizontal Bar
        [0x203F, 0x2040], // Undertie, Character Tie
        [0x2300, 0x23FF], // Miscellaneous Technical
        [0x20A0, 0x20CF], // Currency Symbols
        [0x2200, 0x22FF], // Mathematical Operators
        [0x25A0, 0x25FF], // Geometric Shapes
        [0x2600, 0x26FF], // Miscellaneous Symbols
        [0x27C0, 0x27EF], // Miscellaneous Mathematical Symbols-A
        [0x27F0, 0x27FF], // Supplemental Arrows-A
        [0x2900, 0x297F], // Supplemental Arrows-B
        [0x2980, 0x29FF], // Miscellaneous Mathematical Symbols-B
        [0x2A00, 0x2AFF], // Supplemental Mathematical Operators
        [0x2B00, 0x2BFF], // Miscellaneous Symbols and Arrows
        [0x2000, 0x206F], // General Punctuation
        [0x2100, 0x214F], // Letterlike Symbols
        [0x2190, 0x21FF], // Arrows
        [0x2400, 0x243F], // Control Pictures
        [0x2440, 0x245F], // Optical Character Recognition
        [0x2500, 0x257F], // Box Drawing
        [0x2580, 0x259F], // Block Elements
        [0x2700, 0x27BF], // Dingbats
        [0x2C60, 0x2C7F], // Latin Extended-C
        [0x2E00, 0x2E7F]  // Supplemental Punctuation
        // Thêm các khoảng mã Unicode khác tại đây nếu cần
      ];

      if (singleCodePoints.has(code)) {
        return true;
      }

      // Kiểm tra xem mã Unicode có nằm trong các khoảng mã không
      for (const range of ranges) {
        if (code >= range[0] && code <= range[1]) {
          return true;
        }
      }

      return false;
    }

    const text = new this(textContent, {
        left: left + dx,
        top: top + dy,
        underline: textDecoration.includes('underline'),
        overline: textDecoration.includes('overline'),
        linethrough: textDecoration.includes('line-through'),
        // we initialize this as 0
        strokeWidth: 0,
        fontSize,
        editable: true,
        ...restOfOptions,
      }),
      textHeightScaleFactor = text.getScaledHeight() / text.height,
      lineHeightDiff =
        (text.height + text.strokeWidth) * text.lineHeight - text.height,
      scaledDiff = lineHeightDiff * textHeightScaleFactor,
      textHeight = text.getScaledHeight() + scaledDiff;

    let offX = 0;
    /*
      Adjust positioning:
        x/y attributes in SVG correspond to the bottom-left corner of text bounding box
        fabric output by default at top, left.
    */
    if (textAnchor === CENTER) {
      offX = text.getScaledWidth() / 2;
    }
    if (textAnchor === RIGHT) {
      offX = text.getScaledWidth();
    }
    text.set({
      left: text.left - offX,
      top: text.top - (textHeight - text.fontSize * (0.07 + text._fontSizeFraction)) / text.lineHeight,
      strokeWidth,
    });
    const code = text.text.charCodeAt(0);
    const computerModernFonts = new Set([
      "cmsy10", "cmr10", "cmmi10", "cmmi7", "cmex10", "cmss10", "cmss12",
      "cmtt10", "cmbx10", "cmbxsl10", "cmssl10", "cmfi10", "cmcsc10",
      "cmvtt10", "cmb10", "cmmi12", "cmr12", "cmu10"
    ]);

    if (computerModernFonts.has(text.fontFamily)) {
      text.set({
        fontFamily: 'times new roman'
      });
    }
    return text;
  }

}
Object.assign(IText.prototype, {
  ...TextMixin
})
classRegistry.setClass(IText)
classRegistry.setSVGClass(IText)